// @ts-nocheck
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useTeamRole } from '@/hooks/useTeamRole';
import TeamSearchDialog, { TeamSearchMatch } from '@/components/teams/TeamSearchDialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Loader2, Users, Plus, Check, AlertCircle, ArrowLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

const FALLBACK_AGE_GROUPS = ['10U', '11U', '12U', '13U', '14U', '15U', '16U', '17U', '18U'];

function parseAgeGroups(desc?: string | null): string[] {
  if (!desc) return FALLBACK_AGE_GROUPS;
  const matches = desc.match(/\b\d{1,2}U\b/g);
  if (matches && matches.length) return Array.from(new Set(matches));
  return FALLBACK_AGE_GROUPS;
}

function fmtDate(d?: string | null) {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return d;
  }
}

function Stepper({ step }: { step: number }) {
  const steps = ['Team', 'Division', 'Extras', 'Confirm'];
  return (
    <div className="flex items-center justify-between gap-2 mb-6">
      {steps.map((label, i) => {
        const n = i + 1;
        const active = step === n;
        const done = step > n;
        return (
          <div key={label} className="flex items-center flex-1 last:flex-none">
            <div
              className={cn(
                'flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium transition-colors',
                active && 'bg-primary text-primary-foreground',
                done && 'bg-primary/10 text-primary',
                !active && !done && 'bg-muted text-muted-foreground',
              )}
            >
              <span
                className={cn(
                  'h-5 w-5 rounded-full inline-flex items-center justify-center text-[10px] font-bold',
                  active && 'bg-primary-foreground text-primary',
                  done && 'bg-primary text-primary-foreground',
                  !active && !done && 'bg-background text-muted-foreground border',
                )}
              >
                {done ? <Check className="h-3 w-3" /> : n}
              </span>
              <span className="hidden sm:inline">{label}</span>
            </div>
            {n < steps.length && (
              <div className={cn('h-px flex-1 mx-2', done ? 'bg-primary' : 'bg-border')} />
            )}
          </div>
        );
      })}
    </div>
  );
}

interface DivisionData {
  division: string;
  age_group: string;
  display_name_override: string;
}

interface PricingExtra {
  id: string;
  name: string;
  price: number;
  description?: string;
}

export default function EventRegistrationPage() {
  const { eventSlug } = useParams<{ eventSlug: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [team, setTeam] = useState<TeamSearchMatch | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [requestingAccess, setRequestingAccess] = useState(false);
  const [accessRequestSent, setAccessRequestSent] = useState(false);

  const [divisionData, setDivisionData] = useState<DivisionData>({
    division: '',
    age_group: '',
    display_name_override: '',
  });
  const [extrasQty, setExtrasQty] = useState<Record<string, number>>({});
  const [specialRequests, setSpecialRequests] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [duplicateError, setDuplicateError] = useState(false);

  const eventQuery = useQuery({
    queryKey: ['event-by-slug', eventSlug],
    enabled: !!eventSlug,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('events')
        .select('*')
        .eq('slug', eventSlug)
        .maybeSingle();
      if (error) throw error;
      if (!data) throw new Error('Event not found');
      return data;
    },
  });

  const event = eventQuery.data;
  const { data: role } = useTeamRole(team?.team_id);

  const ageGroupOptions = useMemo(
    () => parseAgeGroups(event?.age_groups_description),
    [event?.age_groups_description],
  );

  // Pre-fill age_group when team selected
  useEffect(() => {
    if (team && !divisionData.age_group) {
      const match = ageGroupOptions.find((g) => g === team.age_group);
      if (match) setDivisionData((d) => ({ ...d, age_group: match }));
    }
  }, [team, ageGroupOptions]); // eslint-disable-line

  // Pricing extras
  const pricingQuery = useQuery({
    queryKey: ['event-pricing', event?.id],
    enabled: !!event?.id && step >= 3,
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from('event_pricing')
        .select('*')
        .eq('event_id', event.id);
      return data ?? [];
    },
  });

  const pricingStruct = pricingQuery.data?.[0]?.pricing_structure ?? {};
  const baseFee = Number(pricingStruct.team_registration_fee ?? 0);
  const extras: PricingExtra[] = Array.isArray(pricingStruct.extras) ? pricingStruct.extras : [];

  const extrasTotal = extras.reduce((sum, ex) => sum + (extrasQty[ex.id] ?? 0) * Number(ex.price ?? 0), 0);
  const srFee = specialRequests.trim() && event?.special_request_fee ? Number(event.special_request_fee) : 0;
  const totalAmount = baseFee + extrasTotal + srFee;

  // Billing contact
  const billingQuery = useQuery({
    queryKey: ['team-billing-contact', team?.team_id],
    enabled: !!team?.team_id && step === 4,
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from('v_teams_with_contacts')
        .select('*')
        .eq('id', team!.team_id)
        .maybeSingle();
      return data;
    },
  });

  const handleTeamSelected = (t: TeamSearchMatch) => {
    setTeam(t);
    setSearchOpen(false);
    setAccessRequestSent(false);
  };

  const handleRequestAccess = async () => {
    if (!team) return;
    setRequestingAccess(true);
    try {
      const { error } = await invokeEdgeFunction('rle-create-or-claim-team', {
        body: { action: 'request_to_manage', team_id: team.team_id },
      });
      if (error) throw error;
      setAccessRequestSent(true);
      toast.success('Access request sent to team owner');
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed to request access');
    } finally {
      setRequestingAccess(false);
    }
  };

  // When team selected and user can register, advance
  useEffect(() => {
    if (team && role?.canRegister && step === 1) {
      setStep(2);
    }
  }, [team, role?.canRegister]); // eslint-disable-line

  const handleSubmit = async () => {
    if (!event || !team || !user) return;
    setSubmitting(true);
    setDuplicateError(false);
    try {
      const billingUserId = billingQuery.data?.primary_billing_user_id ?? user.id;
      const billingEmail = billingQuery.data?.primary_billing_email ?? user.email ?? null;

      const selectedExtras = extras
        .filter((ex) => (extrasQty[ex.id] ?? 0) > 0)
        .map((ex) => ({ id: ex.id, name: ex.name, price: ex.price, qty: extrasQty[ex.id] }));

      const { data: reg, error: insertError } = await (supabase as any)
        .from('event_registrations_v2')
        .insert({
          event_id: event.id,
          team_id: team.team_id,
          display_name_override: divisionData.display_name_override || null,
          division: divisionData.division || null,
          age_group: divisionData.age_group,
          registered_by_user_id: user.id,
          primary_billing_user_id: billingUserId,
          primary_billing_email: billingEmail,
          registration_status: 'registered',
          ownership_status: team.ownership_status,
          total_amount_due: totalAmount,
          total_amount_paid: 0,
          payment_status: 'unpaid',
          organization_id: event.organization_id,
          extras_summary: { items: selectedExtras, base_fee: baseFee },
          special_requests: specialRequests || null,
          special_requests_fee: srFee || null,
        })
        .select('*')
        .single();

      if (insertError) {
        const msg = (insertError.message || '').toLowerCase();
        if (msg.includes('duplicate') || msg.includes('unique') || (insertError as any).code === '23505') {
          setDuplicateError(true);
          return;
        }
        throw insertError;
      }

      const { data: linkData, error: linkError } = await invokeEdgeFunction(
        'rle-create-team-billing-session',
        { body: { event_registration_id: reg.id, amount: totalAmount } },
      );
      if (linkError) throw linkError;

      const url = linkData?.payment_link_url || linkData?.url || linkData?.data?.payment_link_url;
      if (url) {
        window.location.href = url;
      } else {
        toast.success('Registration created');
        navigate(`/teams/${team.stable_slug}?tab=registrations`);
      }
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed to create registration');
    } finally {
      setSubmitting(false);
    }
  };

  if (eventQuery.isLoading) {
    return (
      <div className="container max-w-[720px] mx-auto py-12 space-y-4">
        <Skeleton className="h-8 w-2/3" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (eventQuery.isError || !event) {
    return (
      <div className="container max-w-[720px] mx-auto py-12">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Event not found</AlertTitle>
          <AlertDescription>
            This event doesn't exist or has been removed.{' '}
            <Link to="/events" className="underline">Browse events</Link>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (event.status !== 'published' && !event.is_active) {
    return (
      <div className="container max-w-[720px] mx-auto py-12">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Event not accepting registrations</AlertTitle>
          <AlertDescription>
            {event.name} is not currently open for registration.{' '}
            <Link to={`/events/${event.slug}`} className="underline">View event</Link>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container max-w-[720px] mx-auto py-8 px-4">
      <h1 className="text-2xl font-bold mb-1">Register for {event.name}</h1>
      <p className="text-sm text-muted-foreground mb-6">
        {fmtDate(event.start_date)}
        {event.end_date && event.end_date !== event.start_date ? ` – ${fmtDate(event.end_date)}` : ''}
      </p>

      <Stepper step={step} />

      {/* STEP 1 */}
      {step === 1 && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Users className="h-4 w-4" /> I already have a team
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">Search RLevents for your team</p>
                <Button className="w-full" onClick={() => setSearchOpen(true)}>
                  Find my team
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Plus className="h-4 w-4" /> New team
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">Create a new team and register</p>
                <Button
                  variant="secondary"
                  className="w-full"
                  onClick={() =>
                    navigate(`/teams/new?returnTo=/events/${eventSlug}/register`)
                  }
                >
                  Create team
                </Button>
              </CardContent>
            </Card>
          </div>

          {team && role && !role.canRegister && (
            <Alert className="mt-4" variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Permission required</AlertTitle>
              <AlertDescription className="space-y-3">
                <p>
                  You need permission to register <strong>{team.canonical_name}</strong>. Request
                  access from a team owner or manager.
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={requestingAccess || accessRequestSent}
                  onClick={handleRequestAccess}
                >
                  {requestingAccess && <Loader2 className="h-3 w-3 mr-2 animate-spin" />}
                  {accessRequestSent ? 'Request sent' : 'Request access'}
                </Button>
              </AlertDescription>
            </Alert>
          )}

          <TeamSearchDialog
            open={searchOpen}
            onOpenChange={setSearchOpen}
            organizationId={event.organization_id}
            ageGroupFilter={event.age_groups_description ?? undefined}
            onTeamSelected={handleTeamSelected}
            onCreateNew={() =>
              navigate(`/teams/new?returnTo=/events/${eventSlug}/register`)
            }
            onJoinRequested={() => {
              toast.success('Join request sent');
              setSearchOpen(false);
            }}
          />
        </>
      )}

      {/* STEP 2 */}
      {step === 2 && team && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Division & age group</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-sm bg-muted/40 rounded-md p-3">
              Registering: <strong>{team.canonical_name}</strong>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="division">Division</Label>
              <Input
                id="division"
                value={divisionData.division}
                onChange={(e) => setDivisionData({ ...divisionData, division: e.target.value })}
                placeholder="e.g. Gold, Silver, Open"
              />
              <p className="text-xs text-muted-foreground">e.g. Gold, Silver, Open</p>
            </div>

            <div className="space-y-1.5">
              <Label>Age group *</Label>
              <Select
                value={divisionData.age_group}
                onValueChange={(v) => setDivisionData({ ...divisionData, age_group: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select age group" />
                </SelectTrigger>
                <SelectContent>
                  {ageGroupOptions.map((g) => (
                    <SelectItem key={g} value={g}>{g}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="dnamoverride">Display name (optional)</Label>
              <Input
                id="dnamoverride"
                value={divisionData.display_name_override}
                onChange={(e) =>
                  setDivisionData({ ...divisionData, display_name_override: e.target.value })
                }
              />
              <p className="text-xs text-muted-foreground">
                Leave blank to use '{team.canonical_name}'
              </p>
            </div>

            <div className="flex justify-between pt-2">
              <Button variant="ghost" onClick={() => setStep(1)}>
                <ArrowLeft className="h-4 w-4 mr-1" /> Back
              </Button>
              <Button
                disabled={!divisionData.age_group}
                onClick={() => setStep(3)}
              >
                Continue
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* STEP 3 */}
      {step === 3 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Extras & special requests</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {pricingQuery.isLoading ? (
              <Skeleton className="h-20 w-full" />
            ) : extras.length > 0 ? (
              <div className="space-y-2">
                {extras.map((ex) => (
                  <div
                    key={ex.id}
                    className="flex items-center justify-between gap-3 p-3 border rounded-md"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">{ex.name}</div>
                      <div className="text-xs text-muted-foreground">${Number(ex.price).toFixed(2)}</div>
                    </div>
                    <Select
                      value={String(extrasQty[ex.id] ?? 0)}
                      onValueChange={(v) =>
                        setExtrasQty({ ...extrasQty, [ex.id]: Number(v) })
                      }
                    >
                      <SelectTrigger className="w-20">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 11 }, (_, i) => (
                          <SelectItem key={i} value={String(i)}>{i}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No optional extras for this event.</p>
            )}

            {event.accept_special_requests && (
              <div className="space-y-1.5 pt-2 border-t">
                <Label htmlFor="sr">Special requests (optional)</Label>
                <Textarea
                  id="sr"
                  value={specialRequests}
                  onChange={(e) => setSpecialRequests(e.target.value)}
                  rows={3}
                />
                {event.special_request_instructions && (
                  <p className="text-xs text-muted-foreground">
                    {event.special_request_instructions}
                  </p>
                )}
                {event.special_request_fee > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Fee if used: ${Number(event.special_request_fee).toFixed(2)}
                  </p>
                )}
              </div>
            )}

            <div className="rounded-md bg-muted/40 p-3 space-y-1 text-sm">
              <div className="flex justify-between">
                <span>Base fee</span>
                <span>${baseFee.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span>Extras</span>
                <span>${extrasTotal.toFixed(2)}</span>
              </div>
              {srFee > 0 && (
                <div className="flex justify-between">
                  <span>Special request fee</span>
                  <span>${srFee.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between font-semibold border-t pt-1 mt-1">
                <span>Total</span>
                <span>${totalAmount.toFixed(2)}</span>
              </div>
            </div>

            <div className="flex justify-between pt-2">
              <Button variant="ghost" onClick={() => setStep(2)}>
                <ArrowLeft className="h-4 w-4 mr-1" /> Back
              </Button>
              <Button onClick={() => setStep(4)}>Continue to confirmation</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* STEP 4 */}
      {step === 4 && team && (
        <div className="space-y-4">
          {duplicateError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Already registered</AlertTitle>
              <AlertDescription className="space-y-3">
                <p>Your team is already registered for this event.</p>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => navigate(`/teams/${team.stable_slug}?tab=registrations`)}
                >
                  View existing registration
                </Button>
              </AlertDescription>
            </Alert>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Review your registration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="grid grid-cols-[120px_1fr] gap-y-1.5">
                <span className="text-muted-foreground">Team</span>
                <span className="font-medium">{team.canonical_name}</span>
                <span className="text-muted-foreground">Event</span>
                <span className="font-medium">{event.name}</span>
                <span className="text-muted-foreground">Dates</span>
                <span>{fmtDate(event.start_date)}{event.end_date && event.end_date !== event.start_date ? ` – ${fmtDate(event.end_date)}` : ''}</span>
                <span className="text-muted-foreground">Venue</span>
                <span>
                  {event.venue_name}
                  {event.venue_city ? `, ${event.venue_city}` : ''}
                  {event.venue_state ? ` ${event.venue_state}` : ''}
                </span>
                <span className="text-muted-foreground">Division</span>
                <span>{divisionData.division || 'N/A'}</span>
                <span className="text-muted-foreground">Age group</span>
                <span>{divisionData.age_group}</span>
              </div>

              <div className="border-t pt-3 space-y-1">
                <div className="flex justify-between">
                  <span>Base fee</span>
                  <span>${baseFee.toFixed(2)}</span>
                </div>
                {extras.filter((ex) => (extrasQty[ex.id] ?? 0) > 0).map((ex) => (
                  <div key={ex.id} className="flex justify-between text-muted-foreground">
                    <span>{ex.name} × {extrasQty[ex.id]}</span>
                    <span>${(Number(ex.price) * extrasQty[ex.id]).toFixed(2)}</span>
                  </div>
                ))}
                {srFee > 0 && (
                  <div className="flex justify-between">
                    <span>Special request fee</span>
                    <span>${srFee.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-[18px] font-bold border-t pt-2 mt-2">
                  <span>Total</span>
                  <span>${totalAmount.toFixed(2)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Primary billing contact</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              {billingQuery.isLoading ? (
                <Skeleton className="h-12 w-full" />
              ) : (
                <>
                  <div>
                    <div className="font-medium">
                      {billingQuery.data?.primary_billing_name ?? user?.email ?? 'You'}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {billingQuery.data?.primary_billing_email ?? user?.email}
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {billingQuery.data?.primary_billing_name ?? 'This contact'} will be set as the
                    default payer. Share the payment link with anyone who needs to pay.
                  </p>
                </>
              )}
            </CardContent>
          </Card>

          <div className="flex justify-between">
            <Button variant="ghost" onClick={() => setStep(3)} disabled={submitting}>
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create registration & continue to payment
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
